import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Lock, Plus } from 'lucide-react';

import {
  completeTodo,
  createSubtask,
  reorderSubtasks,
  setSubtaskMode,
  type SubtaskMode,
  type Subtask,
  type Todo,
} from '@/api/eos/todos.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { describeEosError, eosErrorCode } from '@/lib/eos/errors';
import { useToast } from '@/hooks/useToast';

/**
 * Subtasks live under a parent to-do (ADR-023 Wave 2). A subtask is just a
 * Todo with a parent, so completion reuses `completeTodo`. Three extra codes
 * on top of the shared EOS ones surface here — a friendly mapper below covers
 * them, then delegates to `describeEosError`. (The drawer keeps its own copy;
 * a component file can't cleanly export a shared non-component helper.)
 */
function describeTodoError(err: unknown, fallback: string): string {
  switch (eosErrorCode(err)) {
    case 'has_incomplete_subtasks':
      return 'Finish every subtask before completing this to-do.';
    case 'subtask_blocked':
      return 'An earlier subtask has to be done first (sequential mode).';
    case 'nested_subtasks_unsupported':
      return 'Subtasks can only be one level deep.';
    default:
      return describeEosError(err, fallback);
  }
}

interface MemberOption {
  membershipId: string;
  name: string;
}

function ownerLabel(sub: Subtask, nameByMembership: Map<string, string>): string {
  if (sub.ownerMembership) {
    const first = sub.ownerMembership.user.firstName ?? '';
    const last = sub.ownerMembership.user.lastName ?? '';
    const full = `${first} ${last}`.trim();
    if (full) return full;
  }
  if (sub.ownerMembershipId) return nameByMembership.get(sub.ownerMembershipId) ?? 'Unknown member';
  return 'Unassigned';
}

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  const first = (parts[0] ?? '').charAt(0);
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? '').charAt(0) : '';
  return `${first}${last}`.toUpperCase() || '·';
}

export function SubtaskList({ parent, members }: { parent: Todo; members: MemberOption[] }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [newTitle, setNewTitle] = useState('');

  const subtasks = parent.subtasks ?? [];
  const mode: SubtaskMode = parent.subtaskMode ?? 'parallel';
  const sequential = mode === 'sequential';

  const nameByMembership = new Map<string, string>();
  for (const m of members) nameByMembership.set(m.membershipId, m.name);

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'todos'] });
  }

  const addMutation = useMutation({
    mutationFn: (title: string) => createSubtask(parent.id, { title }),
    onSuccess: () => {
      setNewTitle('');
      invalidate();
    },
    onError: (err) => {
      toast.show(describeTodoError(err, 'Could not add the subtask.'), { variant: 'danger' });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => completeTodo(id),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeTodoError(err, 'Could not complete the subtask.'), { variant: 'danger' });
    },
  });

  const modeMutation = useMutation({
    mutationFn: (next: SubtaskMode) => setSubtaskMode(parent.id, next),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeTodoError(err, 'Could not change the mode.'), { variant: 'danger' });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) => reorderSubtasks(parent.id, orderedIds),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeTodoError(err, 'Could not reorder the subtasks.'), { variant: 'danger' });
    },
  });

  function move(index: number, delta: number): void {
    const next = [...subtasks];
    const target = index + delta;
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    reorderMutation.mutate(next.map((s) => s.id));
  }

  function handleAdd(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const title = newTitle.trim();
    if (title) addMutation.mutate(title);
  }

  const busy =
    addMutation.isPending ||
    completeMutation.isPending ||
    modeMutation.isPending ||
    reorderMutation.isPending;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">
          Subtasks{subtasks.length > 0 ? ` (${String(subtasks.length)})` : ''}
        </h3>
        {subtasks.length > 0 ? (
          <div
            role="group"
            aria-label="Subtask mode"
            className="inline-flex overflow-hidden rounded-kw-md border border-kw-border text-[12px]"
          >
            {(['parallel', 'sequential'] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                disabled={modeMutation.isPending}
                onClick={() => {
                  if (mode !== m) modeMutation.mutate(m);
                }}
                className={`px-2.5 py-1 font-medium capitalize transition-colors ${
                  mode === m ? 'bg-kw-cerulean text-white' : 'bg-white text-kw-muted hover:bg-kw-bg'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {subtasks.length === 0 ? (
        <p className="text-[12px] text-kw-muted">
          No subtasks yet. Break this down into ordered steps below.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {subtasks.map((sub, index) => {
            const done = sub.completedAt !== null;
            const name = ownerLabel(sub, nameByMembership);
            return (
              <li
                key={sub.id}
                className={`flex items-center gap-2 rounded-kw-md border px-2.5 py-2 ${
                  sub.blocked ? 'border-kw-border bg-kw-bg' : 'border-kw-border bg-white'
                }`}
              >
                {sequential ? (
                  <span
                    aria-hidden
                    className="grid size-5 shrink-0 place-items-center rounded-kw-full bg-kw-bg text-[11px] font-medium text-kw-muted"
                  >
                    {index + 1}
                  </span>
                ) : null}
                <input
                  type="checkbox"
                  aria-label={`Complete subtask ${sub.title}`}
                  checked={done}
                  disabled={done || sub.blocked || completeMutation.isPending}
                  onChange={() => {
                    completeMutation.mutate(sub.id);
                  }}
                  className="size-4 shrink-0 rounded border-kw-border text-kw-cerulean focus:ring-kw-cerulean/40 disabled:opacity-50"
                />
                <div className="min-w-0 flex-1">
                  <p className={`text-[13px] ${done ? 'text-kw-muted line-through' : 'text-kw-shark'}`}>
                    {sub.title}
                  </p>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-kw-muted">
                    <span
                      aria-hidden
                      className="grid size-4 place-items-center rounded-kw-full bg-kw-cerulean-light text-[9px] font-medium text-kw-cerulean-dark"
                    >
                      {initials(name)}
                    </span>
                    <span className="truncate">{name}</span>
                  </div>
                </div>
                {sub.blocked ? (
                  <Badge variant="warning" className="shrink-0 gap-1">
                    <Lock className="size-3" aria-hidden />
                    Blocked
                  </Badge>
                ) : null}
                <div className="flex shrink-0 flex-col">
                  <button
                    type="button"
                    aria-label={`Move subtask ${sub.title} up`}
                    disabled={index === 0 || reorderMutation.isPending}
                    onClick={() => {
                      move(index, -1);
                    }}
                    className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark disabled:opacity-30"
                  >
                    <ArrowUp className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move subtask ${sub.title} down`}
                    disabled={index === subtasks.length - 1 || reorderMutation.isPending}
                    onClick={() => {
                      move(index, 1);
                    }}
                    className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark disabled:opacity-30"
                  >
                    <ArrowDown className="size-3.5" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {sequential && subtasks.length > 1 ? (
        <p className="text-[11px] text-kw-muted">
          Sequential — each step unlocks only once the one before it is done.
        </p>
      ) : null}

      <form onSubmit={handleAdd} className="flex items-center gap-2">
        <input
          value={newTitle}
          onChange={(e) => {
            setNewTitle(e.target.value);
          }}
          placeholder="Add a subtask…"
          aria-label="New subtask title"
          className="flex-1 rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
        />
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          isLoading={addMutation.isPending}
          disabled={!newTitle.trim() || busy}
        >
          <Plus className="size-3.5" />
          Add
        </Button>
      </form>
    </div>
  );
}
